import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { computed, ref, watchEffect } from 'vue';
import { defineStore } from 'pinia';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import type { EntityTheme } from '@getaccept/lib-shared/src/branding/types/theme';
import { useUIStyling } from '@getaccept/lib-shared/src/branding/composables/ui-styling.composable';
import type { UiStyling } from '@getaccept/lib-shared/src/branding/types/ui-styling';
import { BackgroundType } from '@getaccept/lib-shared/src/branding/types/enums/background-type';
import { DsrService } from '../common/dealroom/api/dsr.service';

export const useThemeStore = defineStore('theme', () => {
  const theme = ref<SigningTheme | Omit<EntityTheme, 'id' | 'entityId' | 'fontSettings'> | null>(
    null
  );

  const { flags } = useLaunchDarkly();

  const hasNewBranding = computed(() => flags.value?.newBranding);

  const uiStyling = ref<UiStyling | undefined>(undefined);

  watchEffect(() => {
    if (!theme.value || !hasNewBranding.value) {
      return;
    }

    const { uiStyling: styling } = useUIStyling(
      theme.value as Omit<EntityTheme, 'id' | 'entityId' | 'fontSettings'> | null
    );

    uiStyling.value = styling.value;
  });

  const loadTheme = async ({
    documentId,
    entityId,
  }: {
    documentId?: string;
    entityId?: string;
  }) => {
    try {
      if (hasNewBranding.value) {
        theme.value = await DsrService.loadTheme({ documentId, entityId });
        return;
      }
      theme.value = await DsrService.loadOldTheme({ documentId, entityId });
    } catch (e) {
      console.error(e);
    }
  };

  const backgroundStyle = computed(() => {
    if (hasNewBranding.value) {
      if (uiStyling.value?.background) {
        const isImageBackground =
          'backgroundType' in theme.value && theme.value.backgroundType === BackgroundType.Image;

        return isImageBackground
          ? `background-image: ${uiStyling.value.background}`
          : `background: ${uiStyling.value.background};`;
      }

      return `background-image: url('/assets/img/background/signing-page-bg.svg');`;
    }

    return theme.value?.backgroundImage
      ? `background-image: url(${theme.value.backgroundImage});`
      : `background-image: url('/assets/img/background/signing-page-bg.svg');`;
  });

  return {
    theme,
    loadTheme,
    uiStyling,
    backgroundStyle,
  };
});
